import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import type {
  AdditionalServiceData,
  FlightOffer,
  ServicesSelection,
} from "@/features/flights/types/types";
import { mapToFlightOffer } from "@/features/flights/utils/mapToFlightOffer";
import { TripType } from "@/types/enums";
import type { FlightSearchType } from "@/types/types";

import { useFlightSearchDataContext } from "../FlightSearchDataContext";

import {
  FlightSelectionPayloadContext,
  type FlightSelectionPayloadContextValue,
} from "./FlightSelectionPayloadContext";

export const FlightSelectionPayloadProvider = ({
  children,
}: PropsWithChildren) => {
  const { searchData } = useFlightSearchDataContext();

  const [selectedDepartureFlightId, setSelectedDepartureFlightId] =
    useState<FlightSelectionPayloadContextValue["selectedDepartureFlightId"]>(
      null,
    );
  const [selectedReturnFlightId, setSelectedReturnFlightId] =
    useState<FlightSelectionPayloadContextValue["selectedReturnFlightId"]>(
      null,
    );

  const [selectedOfferIndex, setSelectedOfferIndex] =
    useState<FlightSelectionPayloadContextValue["selectedOfferIndex"]>(null);

  const [selectedOffer, setSelectedOffer] = useState<FlightOffer | null>(null);

  const [selectedServicesMap, setSelectedServicesMap] = useState<
    Record<string, AdditionalServiceData[]>
  >({});

  const [selectedFlightSearchSource, setSelectedFlightSearchSource] =
    useState<FlightSearchType | null>(null);

  const [promoCode, setPromoCode] =
    useState<FlightSelectionPayloadContextValue["promoCode"]>(null);

  const selectedServices = useMemo<ServicesSelection>(() => {
    if (Object.keys(selectedServicesMap).length === 0) {
      return {
        // We're not able to set anything initially
        // because we don't have the data yet
        mandatoryServices: [],
        optionalServices: [],
      } satisfies ServicesSelection;
    }

    return Object.values(selectedServicesMap)
      .flatMap((servicesData) => servicesData)
      .reduce(
        (acc, { service, mandatory }) => {
          if (mandatory) {
            acc.mandatoryServices.push(service);
          } else {
            acc.optionalServices.push(service);
          }
          return acc;
        },
        {
          mandatoryServices: [],
          optionalServices: [],
        } as ServicesSelection,
      );
  }, [selectedServicesMap]);

  const handleDepartureFlightSelection = useCallback<
    FlightSelectionPayloadContextValue["handleDepartureFlightSelection"]
  >(
    (offer, flightId, queryType) => {
      const flightOffer = mapToFlightOffer([flightId], offer);

      setSelectedOfferIndex(offer.index);
      setSelectedServicesMap({});
      setSelectedDepartureFlightId(flightId);
      setSelectedFlightSearchSource(queryType);

      if (searchData?.tripType === TripType.ONE_WAY) {
        setSelectedOffer(flightOffer);
      }
    },
    [searchData?.tripType],
  );

  const handleReturnFlightSelection = useCallback<
    FlightSelectionPayloadContextValue["handleReturnFlightSelection"]
  >(
    (offer, flightId) => {
      if (!selectedDepartureFlightId)
        throw new Error(
          "Selecting return flight without selected departure flight is not allowed",
        );

      const flightOffer = mapToFlightOffer(
        [selectedDepartureFlightId, flightId],
        offer,
      );

      setSelectedOffer(flightOffer);

      setSelectedServicesMap({});
      setSelectedReturnFlightId(flightId);
    },
    [selectedDepartureFlightId],
  );

  const clearFlightSelection = useCallback<
    FlightSelectionPayloadContextValue["clearFlightSelection"]
  >(() => {
    setSelectedServicesMap({});
    setSelectedDepartureFlightId(null);
    setSelectedReturnFlightId(null);
  }, []);

  const getTravellerServices = useCallback<
    FlightSelectionPayloadContextValue["getTravellerServices"]
  >(
    (travellerId) => {
      return selectedServicesMap[travellerId] || [];
    },
    [selectedServicesMap],
  );

  const setTravellerServices = useCallback<
    FlightSelectionPayloadContextValue["setTravellerServices"]
  >((travellerId, services) => {
    setSelectedServicesMap((prev) => {
      return {
        ...prev,
        [travellerId]: services,
      };
    });
  }, []);

  const removeTravellerServices = useCallback<
    FlightSelectionPayloadContextValue["removeTravellerServices"]
  >((travellerId) => {
    setSelectedServicesMap((prev) => {
      const { [travellerId]: omitted, ...rest } = prev;
      return rest;
    });
  }, []);

  useEffect(() => {
    const handleNewMessage: WindowEventHandler<
      "travellerremoved" | "travellerdeselected"
    > = (event) => {
      removeTravellerServices(event.detail.travellerId);
    };

    window.addEventListener("travellerremoved", handleNewMessage);
    window.addEventListener("travellerdeselected", handleNewMessage);

    return () => {
      window.removeEventListener("travellerremoved", handleNewMessage);
      window.removeEventListener("travellerdeselected", handleNewMessage);
    };
  }, [removeTravellerServices]);

  // Memoize the context value to prevent infinite re-renders
  const contextValue = useMemo(
    () => ({
      selectedOfferIndex,
      selectedFlightPayload: selectedOffer
        ? { ...selectedOffer, ...selectedServices }
        : null,
      selectedDepartureFlightId,
      selectedReturnFlightId,
      selectedFlightSearchSource,
      promoCode,
      handleDepartureFlightSelection,
      handleReturnFlightSelection,
      clearFlightSelection,
      getTravellerServices,
      setTravellerServices,
      removeTravellerServices,
      setPromoCode,
    }),
    [
      selectedOfferIndex,
      selectedOffer,
      selectedServices,
      selectedDepartureFlightId,
      selectedReturnFlightId,
      selectedFlightSearchSource,
      promoCode,
      handleDepartureFlightSelection,
      handleReturnFlightSelection,
      clearFlightSelection,
      getTravellerServices,
      setTravellerServices,
      removeTravellerServices,
    ],
  );

  return (
    <FlightSelectionPayloadContext.Provider value={contextValue}>
      {children}
    </FlightSelectionPayloadContext.Provider>
  );
};
